import { useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { useEffect } from 'react'
import type { Socket } from 'socket.io-client'

import { api } from '@/api/client'
import { disconnectSocket, getSocket } from '@/api/socket'

import { ME_KEY, ME_URL } from './use-user'

/** rechecks the user when the server revokes the session, so ProtectedRoute sends them to /login. */
export function useSessionRevokedRedirect(): void {
  const queryClient = useQueryClient()

  useEffect(() => {
    const recheck = () =>
      void queryClient.invalidateQueries({ queryKey: ME_KEY, exact: true })

    const interceptor = api.interceptors.response.use(undefined, error => {
      // the recheck itself hits ME_URL, so skipping it keeps a 401 from looping
      if (
        isAxiosError(error) &&
        error.response?.status === 401 &&
        error.config?.url !== ME_URL
      ) {
        recheck()
      }
      return Promise.reject(error)
    })

    const socket = getSocket()
    const onDisconnect = (reason: Socket.DisconnectReason) => {
      if (reason !== 'io server disconnect') return
      // socket.io never reconnects after a server disconnect
      disconnectSocket()
      recheck()
    }
    socket.on('disconnect', onDisconnect)

    return () => {
      api.interceptors.response.eject(interceptor)
      socket.off('disconnect', onDisconnect)
    }
  }, [queryClient])
}
